/* eslint-disable import/no-named-as-default */
/* eslint-disable camelcase */

// node modules
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { DOMParser } from 'xmldom';
import classNames from 'classnames';

// app modules: components
import CategoryPills from 'components/CategoryPills';
import ContentRawDetails from 'components/ContentRawDetails';
import CopyrightInfo from 'components/CopyrightInfo';
import Deobfuscator from 'components/ContentRaw/Deobfuscator';
import GeneralContent from 'components/GeneralContent';
import RecipeGallery from 'components/RecipeGallery';
import ShopForIngredients from 'components/ShopForIngredients';
import ShowMore from 'components/ShowMore';
import withStyles from 'isomorphic-style-loader/withStyles';

import RecipeConvert from 'components/RecipeConvert';
import RecipeConversion from 'components/RecipeConversion';

// app modules: props
import AuthorsProps from 'components/Authors';
import RecipeImageProps from 'components/RecipeImage/RecipeImageProps';

// app modules: services
import formatter from 'services/formatter';

// assets
import ContentRawCss from './ContentRaw.scss';

const parser = new DOMParser();

class ContentRaw extends Component {
  static propTypes = {
    id: PropTypes.string.isRequired,
    contents: PropTypes.arrayOf(PropTypes.string).isRequired,
    raw: PropTypes.shape({
      intro: PropTypes.string,
      ingredients: PropTypes.arrayOf(PropTypes.string),
      procedures: PropTypes.arrayOf(PropTypes.string),
      tools: PropTypes.string,
    }),
    image: PropTypes.shape({
      credit: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
      primary: PropTypes.string,
      permission: PropTypes.bool,
    }),
    book: PropTypes.shape({
      signature_: PropTypes.string,
      title: PropTypes.string,
      image_name: PropTypes.string,
      image_credit: PropTypes.string,
      publication_date: PropTypes.string,
      recipe_copyright_line: PropTypes.string,
      licensor: PropTypes.string,
      authors: AuthorsProps.authors,
    }),
    graphics: RecipeImageProps.graphics,
    categories: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        title: PropTypes.string,
        type: PropTypes.string,
      }),
    ),
    highlightAdjustments: PropTypes.bool,
    loading: PropTypes.bool,
    isPreview: PropTypes.bool,
    instagramHtml: PropTypes.string,
    instagramWidth: PropTypes.number,
  };

  static defaultProps = {
    raw: {
      intro: null,
      ingredients: null,
      procedures: null,
      tools: '',
    },
    image: {
      credit: '',
      primary: '',
      permission: false,
    },
    book: {
      authors: [{}],
    },
    graphics: [],
    categories: [],
    highlightAdjustments: false,
    loading: false,
    isPreview: false,
    instagramHtml: null,
    instagramWidth: null,
  };

  static keyContent(id, index) {
    return `content-raw-${id}-${index}`;
  }

  static keyGallery(id, index) {
    return `content-raw-gallery-${id}-${index}`;
  }

  static isPhotoForGallery(content) {
    try {
      if (content.indexOf('<figure') === 0) {
        const figureEl = parser.parseFromString(content, 'text/xml').documentElement;
        return (
          figureEl.getAttribute('graphictype') !== 'illustration' &&
          figureEl.getAttribute('float') !== 'inline'
        );
      }
    } catch (err) {
      // Ignore
    }
    return false;
  }

  static getImages(figures) {
    return figures.map(figure => {
      const result = {
        src: '',
        caption: '',
        type: 'photo',
      };
      try {
        const DOM = parser.parseFromString(figure, 'text/xml').documentElement;

        Array.from(DOM.childNodes).forEach(node => {
          if (node.tagName) {
            switch (node.tagName) {
              case 'img':
                result.src = node.getAttribute('src');
                break;

              case 'figcaption':
                result.caption = <span>{node.textContent}</span>;
                break;

              default:
                break;
            }
          }
        });
      } catch (err) {
        // Ignore
      }

      return result;
    });
  }

  static hasInlinePhoto(content) {
    if (content) {
      try {
        const DOM = parser.parseFromString(content, 'application/xml');
        const firstFigure = DOM.getElementsByTagName('figure')[0];

        if (firstFigure) {
          return firstFigure.getAttribute('graphictype') !== 'illustration';
        }
      } catch (e) {
        // Ignore
      }
    }

    return false;
  }

  static removeEveryImage(DOM) {
    formatter.xml.removeAll(DOM, 'img');
  }

  static parseLines(value) {
    return value.replace(/(\\n)/g, '\n');
  }

  constructor(props) {
    super(props);

    this.bound = {
      removePrimaryImage: this.removePrimaryImage.bind(this),
    };
  }

  removePrimaryImage(DOM) {
    const { image } = this.props;

    Array.from(DOM.getElementsByTagName('img')).forEach(img => {
      const src = img.getAttribute('src');
      // check if image object passed from recipe is of type primary to avoid displaying primary photos inline
      if (src === image.primary && image.type === 'primary') {
        img.parentNode.removeChild(img);
      }
    });
  }

  renderPreparation() {
    const {
      id,
      raw,
      image,
      categories,
      highlightAdjustments,
      loading,
      graphics,
      isPreview,
    } = this.props;

    const { tools } = this.props.raw;

    const rawIngredients = raw.ingredients;

    const key = `content-raw-preparation-${id}-${loading}-${!!raw.ingredients}-${!!raw.procedures}`;
    if (!loading) {
      // When loading we'll always show the 2 column layout.
      // After we've loaded, we might switch to 1 column depending on the data available in the recipe.
      if (rawIngredients === null) {
        return (
          <section className="recipe__section" key={key}>
            <ContentRawDetails
              heading="Method"
              raw={raw.procedures}
              image={image}
              highlightAdjustments={highlightAdjustments}
              loading={loading}
              graphics={graphics}
            />
            {!isPreview ? <ShopForIngredients recipeId={id} /> : null}
          </section>
        );
      } else if (raw.procedures === null) {
        return (
          <section className="recipe__section" key={key}>
            <ContentRawDetails
              heading="Ingredients"
              raw={rawIngredients}
              image={image}
              highlightAdjustments={highlightAdjustments}
              loading={loading}
              graphics={graphics}
            />
            {tools ? <GeneralContent raw={tools} graphics={graphics} /> : ''}
            {!isPreview ? <ShopForIngredients recipeId={id} /> : null}
          </section>
        );
      }
    }

    const hasWideIngredients = rawIngredients
      ? rawIngredients.some(
          ingredientPart =>
            ingredientPart !== null &&
            typeof ingredientPart === 'string' &&
            ingredientPart.includes('<table '),
        )
      : false;

    // const hasWideIngredients = true;
    const columnClassNames = classNames('recipe__column', 'recipe__section', {
      'recipe__column--wide': hasWideIngredients,
    });

    return (
      <>
        {rawIngredients && <RecipeConversion />}
        <div
          className={classNames('recipe__columns', {
            'recipe__columns--wide': hasWideIngredients,
          })}
          key={key}
        >
          <section className={columnClassNames}>
            <ContentRawDetails
              heading="Ingredients"
              raw={rawIngredients}
              image={image}
              highlightAdjustments={highlightAdjustments}
              loading={loading}
              dualColumn
              graphics={graphics}
              contentRawAction={<RecipeConvert />}
            />
            {tools ? <GeneralContent raw={tools} graphics={graphics} /> : ''}
            {!isPreview ? <ShopForIngredients recipeId={id} className="margin-bottom-25" /> : null}
            <CategoryPills categories={categories} backText="Recipe" />
          </section>
          <section className={columnClassNames}>
            <ContentRawDetails
              heading="Method"
              raw={raw.procedures}
              image={image}
              highlightAdjustments={highlightAdjustments}
              loading={loading}
              dualColumn
              graphics={graphics}
            />
          </section>
        </div>
      </>
    );
  }

  render() {
    const {
      book,
      categories,
      contents,
      graphics,
      highlightAdjustments,
      id,
      image,
      instagramHtml,
      instagramWidth,
      isPreview,
      loading,
      raw,
    } = this.props;
    let iterator = 0;
    const contentsLength = contents.length;

    const results = [];
    const beforeTransform = image.permission
      ? this.bound.removePrimaryImage
      : ContentRaw.removeEveryImage;

    while (iterator < contentsLength) {
      const content = contents[iterator];
      // check for images that go to the gallery
      const isPhotoForGallery = ContentRaw.isPhotoForGallery(content);
      // check for inline images that shouldn't go to the gallery
      const hasInlinePhoto = ContentRaw.hasInlinePhoto(content);

      if (isPhotoForGallery) {
        // Fall through
      } else if (hasInlinePhoto) {
        results.push(
          <GeneralContent
            key={ContentRaw.keyContent(id, iterator)}
            raw={content}
            beforeTransform={[beforeTransform]}
            highlightAdjustments={highlightAdjustments}
            loading={loading}
            skeletonProps={{ width: '100%', count: 3 }}
            graphics={graphics}
          />,
        );
      } else {
        switch (content) {
          case 'subsection#intro':
            results.push(
              <ShowMore
                childrenLength={formatter.truncation.getElementLength(raw.intro)}
                key="intro"
                className="margin-bottom-20"
              >
                <GeneralContent
                  raw={raw.intro}
                  beforeTransform={[beforeTransform]}
                  highlightAdjustments={highlightAdjustments}
                  loading={loading}
                  skeletonProps={{ width: '100%', count: 2 }}
                  graphics={graphics}
                />
              </ShowMore>,
            );
            break;

          case 'subsection#preparation':
            results.push(this.renderPreparation());
            break;

          default:
            results.push(
              <GeneralContent
                key={ContentRaw.keyContent('content', iterator)}
                raw={content}
                beforeTransform={[beforeTransform]}
                highlightAdjustments={highlightAdjustments}
                loading={loading}
                skeletonProps={{ width: '100%', count: 3 }}
                graphics={graphics}
              />,
            );
            break;
        }
      }

      iterator += 1;
    }

    if (!isPreview) {
      // Normally the categories will appear at the end of the ingredients column.
      // However, if there aren't any ingredients then render it here at the end of the document.
      if (!raw.ingredients || !raw.procedures) {
        results.push(<CategoryPills key="categories" categories={categories} backText="Recipe" />);
      }

      results.push(<CopyrightInfo book={book} key={ContentRaw.keyContent('copyright', 0)} />);
    }

    const images = graphics.filter(graphic => graphic.gallery);
    if (images.length > 0) {
      results.push(
        <RecipeGallery
          key={ContentRaw.keyGallery(id, iterator)}
          images={images}
          instagramHtml={instagramHtml}
          instagramWidth={instagramWidth}
        />,
      );
    }
    return <Deobfuscator book={book}>{results}</Deobfuscator>;
  }
}

export default withStyles(ContentRawCss)(ContentRaw);
